import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';

const RoomList = () => {
    // State to hold room data fetched from the API
    const [rooms, setRooms] = useState([]);
    // State to hold hotel booking data
    const [hotelBookings, setHotelBookings] = useState([]);
    // State to hold guest data
    const [guestData, setGuestData] = useState([]);
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    const roomsPerPage = 8; // Assuming 8 rooms per page based on data provided in screenshot

    // State for Add/Edit Modal
    const [addEditModalOpen, setAddEditModalOpen] = useState(false);
    const [currentRoom, setCurrentRoom] = useState(null); // Holds data of room being edited, null for add
    const [formData, setFormData] = useState({}); // Holds form input data

    // Fetch all necessary data when the component mounts or when a change requires re-fetching
    useEffect(() => {
        fetchData();
    }, []);

    // Function to fetch rooms, hotel bookings, and guest data from the backend
    const fetchData = async () => {
        try {
            const [roomsResponse, hotelBookingsResponse, guestResponse] = await Promise.all([
                axios.get('https://admin.thepridehotel.in/api/rooms'),
                axios.get('https://admin.thepridehotel.in/api/hotel_bookings'),
                axios.get('https://admin.thepridehotel.in/api/guest') // Fetch guest data
            ]);

            const hotelBookingsData = hotelBookingsResponse.data;
            const guestTableData = guestResponse.data;

            setHotelBookings(hotelBookingsData);
            setGuestData(guestTableData);

            const formattedData = roomsResponse.data.map(room => {
                let derivedStatus = 'Available'; // Default to Available for the derived 'Status' column

                // Find all guest bookings for this specific room number
                const guestBookingsForThisRoom = guestTableData.filter(
                    g => String(g.room_number) === String(room.room_number)
                );

                // Check if this room is currently associated with a booking that makes it 'Available'
                // (i.e., the hotel_booking status is 'Cancelled' or 'Checked Out')
                const isAvailableDueToBookingStatus = guestBookingsForThisRoom.some(gBooking => {
                    const linkedHb = hotelBookingsData.find(hb => String(hb.id) === String(gBooking.reservation_id));
                    return linkedHb && (linkedHb.status === 'Cancelled' || linkedHb.status === 'Checked Out');
                });

                if (isAvailableDueToBookingStatus) {
                    derivedStatus = 'Available';
                } else {
                    // If not made available by cancellation/checkout, find the most recent active guest booking
                    let latestActiveGuestBooking = null;
                    for (const gBooking of guestBookingsForThisRoom) {
                        const linkedHb = hotelBookingsData.find(hb => String(hb.id) === String(gBooking.reservation_id));
                        // Consider only hotel bookings that are not 'Cancelled' or 'Checked Out' as "active"
                        if (linkedHb && linkedHb.status !== 'Cancelled' && linkedHb.status !== 'Checked Out') {
                            if (!latestActiveGuestBooking || new Date(gBooking.submitted_at) > new Date(latestActiveGuestBooking.submitted_at)) {
                                latestActiveGuestBooking = gBooking;
                            }
                        }
                    }

                    if (latestActiveGuestBooking) {
                        // If an active guest booking is found, use its status
                        derivedStatus = latestActiveGuestBooking.status;
                    }
                    // If no active booking is found (after excluding cancelled/checked out), it remains 'Available'
                    // as per the initial default, which is correct.
                }

                return {
                    id: room.id,
                    roomNumber: room.room_number,
                    bedType: room.bed_type, // Keep bedType if it's still relevant for other parts of the app but not displayed or edited
                    roomFloor: room.room_floor,
                    roomFacility: room.room_facility,
                    status: derivedStatus, // This is the derived 'Occupied/Available' status from bookings
                    roomStatus: room.room_status || '', // ENSURE room_status IS A STRING
                    roomType: room.room_type,
                };
            });
            setRooms(formattedData);
        } catch (error) {
            console.error('Error fetching data:', error);
            alert('Failed to load rooms or bookings. Please ensure the backend server is running.');
        }
    };

    // Filtered rooms based on the selected filter
    const filteredRooms = useMemo(() => {
        switch (filter) {
            case 'all': return rooms;
            case 'available': return rooms.filter(r => r.status === 'Available');
            case 'booked': return rooms.filter(r => r.status === 'Booked');
            case 'reserved': return rooms.filter(r => r.status === 'Reserved');
            case 'waitlist': return rooms.filter(r => r.status === 'Waitlist');
            case 'blocked': return rooms.filter(r => r.status === 'Blocked');
            case 'checked in': return rooms.filter(r => r.status === 'Checked In');
            case 'new': return rooms.filter(r => r.status === 'New');
            case 'pending': return rooms.filter(r => r.status === 'Pending');
            case 'paid': return rooms.filter(r => r.status === 'Paid');
            default: return rooms;
        }
    }, [filter, rooms]);

    const totalPages = Math.ceil(filteredRooms.length / roomsPerPage);
    const currentRooms = filteredRooms.slice((page - 1) * roomsPerPage, page * roomsPerPage);

    // Handler for opening "Add Room" modal
    const handleAddRoomClick = () => {
        setCurrentRoom(null); // Clear current room for 'add' mode
        setFormData({ // Initialize form fields for a new room
            roomNumber: '',
            bedType: '', // Re-added Bed Type
            roomFloor: '',
            roomFacility: '',
            status: 'Available', // Default derived status for new rooms
            roomStatus: 'clean', // Default cleanliness status for new rooms
            roomType: '',
        });
        setAddEditModalOpen(true);
    };

    // Handler for opening "Edit Room" modal
    const handleEditClick = (room) => {
        setCurrentRoom(room); // Set the room being edited
        setFormData({ // Populate form with existing room data
            roomNumber: room.roomNumber,
            bedType: room.bedType, // Re-added Bed Type
            roomFloor: room.roomFloor,
            roomFacility: room.roomFacility,
            status: room.status, // Derived status
            roomStatus: room.roomStatus, // Cleanliness status from DB
            roomType: room.roomType,
        });
        setAddEditModalOpen(true);
    };

    // Handler for input changes in the form
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handler for submitting the form (Add or Update)
    const handleSubmit = async () => {
        // Client-side validation
        // Re-added bedType to validation
        const { roomNumber, bedType, roomFloor, roomFacility, status, roomStatus, roomType } = formData;
        if (!roomNumber || !bedType || !roomFloor || !roomFacility || !status || !roomStatus || !roomType) {
            alert('Please fill in all required fields.');
            return;
        }

        try {
            if (currentRoom) {
                // Update existing room
                await axios.put(`https://admin.thepridehotel.in/api/rooms/${currentRoom.id}`, {
                    room_number: roomNumber, // Ensure backend uses snake_case if that's its expectation
                    bed_type: bedType, // Re-added Bed Type to payload
                    room_floor: roomFloor,
                    room_facility: roomFacility,
                    status: status, // Derived status
                    room_status: roomStatus, // Cleanliness status
                    room_type: roomType
                });
                alert('Room updated successfully!');
            } else {
                // Add new room
                await axios.post('https://admin.thepridehotel.in/api/rooms', {
                    room_number: roomNumber,
                    bed_type: bedType, // Re-added Bed Type to payload
                    room_floor: roomFloor,
                    room_facility: roomFacility,
                    status: status,
                    room_status: roomStatus,
                    room_type: roomType
                });
                alert('Room added successfully!');
            }
            setAddEditModalOpen(false); // Close modal
            fetchData(); // Re-fetch data to update the table and statuses
        } catch (error) {
            console.error('Error saving room:', error);
            if (error.response && error.response.data && error.response.data.error) {
                alert(`Error: ${error.response.data.error}`);
            } else {
                alert('Failed to save room. Please check console for details.');
            }
        }
    };

    // Internal Styles for RoomList - Meticulously matched to the screenshot
    const styles = {
      // Global container styles for the entire card
      container: {
        fontFamily: "'Open Sans', sans-serif", // Ensure font family is correctly applied
        backgroundColor: '#fff',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
        padding: '30px', // Matches padding around the content
        width: '100%',
        maxWidth: '12200px', // Max width for the entire card
        boxSizing: 'border-box',
      },

      // Header section styles (for "Room" title)
      header: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start', // Align "Room" title to the left
        marginBottom: '25px', // Space between header and filter/add room row
      },
      headerTitle: {
        fontSize: '16px', // Smaller font size for "Room" title
        color: '#6B7280', // Lighter gray color for "Room" title
        fontWeight: '500', // Adjusted font weight
        margin: '0',
        marginBottom: '20px', // Space between "Room" title and filter buttons
      },

      // Top control row (filter buttons and "Add room" button)
      topControlsRow: {
        display: 'flex',
        justifyContent: 'space-between', // Pushes filter buttons to left, add room to right
        alignItems: 'center',
        width: '100%',
        marginBottom: '20px', // Space below this row before the table
      },

      // Filter buttons container
      filterButtons: {
        display: 'flex',
        gap: '10px', // Gap between individual filter buttons
        flexWrap: 'wrap', // Allow wrapping on smaller screens if needed
      },
      filterBtn: {
        padding: '7px 16px',
        fontSize: '14px',
        color: '#4B5563', // Default text color
        backgroundColor: '#fff',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#D1D5DB',
        borderRadius: '20px', // More rounded as per image
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease', // Smooth transition
        whiteSpace: 'nowrap', // Prevent text from wrapping inside button
      },
      filterBtnActive: {
        backgroundColor: '#E0F2FE', // Light blue background for active
        color: '#3B82F6', // Blue text color for active
        fontWeight: '600',
        borderColor: '#3B82F6', // Blue border for active
      },
      filterBtnHover: { // For non-active buttons on hover
          backgroundColor: '#f3f4f6',
      },

      // Add Room Button
      addRoomBtn: {
        backgroundColor: '#AE7D54', // Specific brown/orange color from image
        color: 'white',
        padding: '8px 18px',
        border: 'none',
        borderRadius: '8px', // Slightly less rounded than filter buttons
        fontSize: '14px',
        cursor: 'pointer',
        fontWeight: '500',
        transition: 'background-color 0.2s ease',
      },
      addRoomBtnHover: {
          backgroundColor: '#9A6B46', // Slightly darker on hover
      },

      // Table container wrapper
      tableContainer: {
        overflowX: 'auto', // Allows horizontal scrolling for small screens
        border: '1px solid #E5E7EB', // Border around the entire table wrapper
        borderRadius: '12px', // Rounded corners for the table wrapper
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)', // Subtle shadow for the table wrapper
      },
      table: {
        width: '100%',
        borderCollapse: 'collapse', // Collapse borders between cells
        minWidth: '900px', // Ensures table doesn't get too squished on narrow views
        fontSize: '14px',
      },

      // Table header (th) styles
      tableHeader: {
        textAlign: 'left',
        padding: '16px 20px', // Padding inside header cells
        color: '#6B7280', // Header text color
        fontWeight: '600',
        borderBottom: '1px solid #E5E7EB', // Separator below header
        backgroundColor: '#F9FAFB', // Background for the header row
      },

      // Table data cells (td) styles
      tableCell: {
        padding: '16px 20px', // Padding inside data cells
        color: '#111827', // Data row text color
        borderBottom: '1px solid #F3F4F6', // Light separator between data rows
        whiteSpace: 'nowrap', // Prevent text wrapping in cells (important for facility column)
      },
      // Note: `tableRowLastChildCell` is handled directly in JSX using conditional styling
      tableRowHover: { // For tr:hover
        backgroundColor: '#F9FAFB', // Light gray background on hover
      },

      // Status badges
      status: {
        padding: '5px 12px',
        borderRadius: '999px', // Fully rounded (pill shape)
        fontWeight: '500',
        fontSize: '13px',
        display: 'inline-block', // Ensures padding and background apply correctly
        textTransform: 'capitalize', // Capitalize the first letter
      },
      statusAvailable: {
        backgroundColor: '#E0F2FE', // Light blue
        color: '#3B82F6', // Blue text
      },
      statusBooked: {
        backgroundColor: '#FFE4E6', // Light red
        color: '#EF4444', // Red text
      },
      statusReserved: {
        backgroundColor: '#DCFCE7', // Light green
        color: '#22C55E', // Green text
      },
      statusWaitlist: {
        backgroundColor: '#FEF3C7', // Light yellow
        color: '#F59E0B', // Yellow/orange text
      },
      statusBlocked: {
        backgroundColor: '#F3F4F6', // Light gray
        color: '#6B7280', // Gray text
      },
      // New styles for statuses derived from guest table
      statusNew: {
        backgroundColor: '#D1FAE5', // Light green
        color: '#10B981', // Green text
      },
      statusCheckedIn: {
        backgroundColor: '#E0F2FE', // Light blue
        color: '#3B82F6', // Blue text
      },
      statusPending: {
        backgroundColor: '#FEF3C7', // Amber
        color: '#D97706', // Darker Amber text
      },
      statusPaid: {
        backgroundColor: '#D1FAE5', // Emerald
        color: '#059696', // Darker Emerald text
      },
      // Styles for room_status (cleanliness)
      roomStatusClean: {
        backgroundColor: '#D1FAE5', // Light green
        color: '#059669', // Green text (similar to Paid)
      },
      roomStatusDirty: {
        backgroundColor: '#FFE4E6', // Light red (similar to Booked)
        color: '#EF4444', // Red text
      },
      roomStatusService: {
        backgroundColor: '#FEF3C7', // Light yellow (similar to Waitlist)
        color: '#F59E0B', // Yellow/orange text
      },

      // Ellipsis icon (more actions)
      ellipsisIcon: {
        color: '#9CA3AF', // Gray color
        cursor: 'pointer',
        transition: 'color 0.2s ease', // Smooth color change on hover
      },
      ellipsisIconHover: {
        color: '#4B5563', // Darker gray on hover
      },

      // Pagination
      pagination: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '25px', // Space above pagination
      },
      paginationBtn: {
        backgroundColor: '#F9FAFB',
        color: '#4B5563',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#D1D5DB',
        padding: '8px 14px',
        borderRadius: '6px',
        fontSize: '14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '5px', // Space between icon and text
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      },
      paginationBtnHover: {
          backgroundColor: '#E5E7EB', // Slightly darker gray on hover
          color: '#374151', // Darker text on hover
      },
      paginationBtnDisabled: {
        opacity: '0.5',
        cursor: 'not-allowed',
        // Important: Reset hover styles when disabled to match screenshot behavior
        backgroundColor: '#F9FAFB',
        color: '#4B5563',
      },

      pageNumbers: {
        display: 'flex',
        gap: '6px', // Space between page numbers
      },
      pageNumber: {
        padding: '8px 14px',
        borderRadius: '66px',
        backgroundColor: '#fff',
        color: '#6B7280',
        fontWeight: '500',
        cursor: 'pointer',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#E5E7EB',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      },
      pageNumberActive: {
        backgroundColor: '#3B82F6', // Blue from the active filter button
        color: 'white',
        borderColor: '#3B82F6', // Blue border for active
      },
      pageNumberHover: { // Hover for non-active page numbers
          backgroundColor: '#F3F4F6',
          color: '#4B5563',
      },
      // Modal Styles (copied from RatesTable, ensure consistency)
      modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      },
      modalContent: {
        backgroundColor: '#fff',
        padding: '30px',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '450px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'relative',
      },
      modalCloseButton: {
        position: 'absolute',
        top: '15px',
        right: '15px',
        backgroundColor: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#9CA3AF',
        lineHeight: 1,
      },
      modalLabel: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' },
      modalInput: {
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #D1D5DB',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:focus': {
          borderColor: '#AE7D54',
          boxShadow: '0 0 0 2px rgba(174, 125, 84, 0.2)',
        }
      },
      modalSelect: { // Added style for select input
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #D1D5DB',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        cursor: 'pointer',
      },
      modalButtonsContainer: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
      modalCancelBtn: {
        padding: '10px 20px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#D1D5DB',
        backgroundColor: '#fff',
        color: '#374151',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
      },
      modalSaveBtn: {
        padding: '10px 20px',
        backgroundColor: '#AE7D54',
        color: '#fff',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'background-color 0.2s ease',
      }
    };

    const getStatusStyleForRoom = (status) => {
      switch ((status || '').toLowerCase()) {
          case 'available': return styles.statusAvailable;
          case 'booked': return styles.statusBooked;
          case 'reserved': return styles.statusReserved;
          case 'checked in': return styles.statusCheckedIn;
          case 'new': return styles.statusNew;
          case 'pending': return styles.statusPending;
          case 'paid': return styles.statusPaid;
          case 'waitlist': return styles.statusWaitlist;
          case 'blocked': return styles.statusBlocked;
          default: return {};
      }
    };

    const getRoomStatusStyle = (roomStatus = '') => {
        switch (roomStatus.toLowerCase()) {
            case 'clean': return styles.roomStatusClean;
            case 'durty': return styles.roomStatusDirty; // Assuming 'durty' is intentional based on user's table data
            case 'service': return styles.roomStatusService;
            default: return {};
        }
    };

    return (
        <div style={styles.container}>
            {/* IMPORTANT: For 100% fidelity, include the font import in your public/index.html */}
            {/* Example in public/index.html, inside the <head> tag: */}
            {/* <link href="https://fonts.googleapis.com/css2?family=Open+Sans&display=swap" rel="stylesheet"> */}

            <div style={styles.header}>
                <h1 style={styles.headerTitle}>Room</h1>
                <div style={styles.topControlsRow}>
                    <div style={styles.filterButtons}>
                        <button
                          style={{
                            ...styles.filterBtn,
                            ...(filter === 'all' ? styles.filterBtnActive : {}),
                          }}
                          onClick={() => setFilter('all')}
                          onMouseOver={(e) => { if (filter !== 'all') e.currentTarget.style.backgroundColor = styles.filterBtnHover.backgroundColor; }}
                          onMouseOut={(e) => { if (filter !== 'all') e.currentTarget.style.backgroundColor = styles.filterBtn.backgroundColor; }}
                        >
                            All room({rooms.length})
                        </button>
                        <button
                          style={{
                            ...styles.filterBtn,
                            ...(filter === 'available' ? styles.filterBtnActive : {}),
                          }}
                          onClick={() => setFilter('available')}
                           onMouseOver={(e) => { if (filter !== 'available') e.currentTarget.style.backgroundColor = styles.filterBtnHover.backgroundColor; }}
                           onMouseOut={(e) => { if (filter !== 'available') e.currentTarget.style.backgroundColor = styles.filterBtn.backgroundColor; }}
                        >
                            Available room({rooms.filter(r => r.status === 'Available').length})
                        </button>
                        <button
                          style={{
                            ...styles.filterBtn,
                            ...(filter === 'booked' ? styles.filterBtnActive : {}),
                          }}
                          onClick={() => setFilter('booked')}
                          onMouseOver={(e) => { if (filter !== 'booked') e.currentTarget.style.backgroundColor = styles.filterBtnHover.backgroundColor; }}
                          onMouseOut={(e) => { if (filter !== 'booked') e.currentTarget.style.backgroundColor = styles.filterBtn.backgroundColor; }}
                        >
                            Booked({rooms.filter(r => r.status === 'Booked').length})
                        </button>
                        {/* Add more filter buttons if needed for other statuses like 'New', 'Checked In', 'Pending', 'Paid' */}
                    </div>
                    <button
                        style={styles.addRoomBtn}
                        onClick={handleAddRoomClick}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.addRoomBtnHover.backgroundColor}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.addRoomBtn.backgroundColor}
                    >
                        Add room
                    </button>
                </div>
            </div>

            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.tableHeader}>Room number</th>
                            <th style={styles.tableHeader}>Bed Type</th> {/* ADDED */}
                            <th style={styles.tableHeader}>Room Status</th> {/* This will show cleanliness: clean/dirty/service */}
                            <th style={styles.tableHeader}>Room floor</th>
                            <th style={styles.tableHeader}>Room facility</th>
                            <th style={styles.tableHeader}>Status</th> {/* This will show booking status: Occupied/Available */}
                            <th style={styles.tableHeader}>Room Type</th>
                            <th style={styles.tableHeader}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRooms.map((room, index) => (
                            <tr
                                key={room.id}
                                style={{
                                    ...(index === currentRooms.length - 1 ? { borderBottom: 'none' } : { borderBottom: styles.tableCell.borderBottom }),
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <td style={styles.tableCell}>#{room.roomNumber}</td>
                                <td style={styles.tableCell}>{room.bedType}</td> {/* ADDED */}
                                <td style={styles.tableCell}> {/* Display room_status (clean/dirty/service) here */}
                                  <span style={{ ...styles.status, ...getRoomStatusStyle(room.roomStatus) }}>
                                    {room.roomStatus}
                                  </span>
                                </td>
                                <td style={styles.tableCell}>{room.roomFloor}</td>
                                <td style={styles.tableCell}>{room.roomFacility}</td>
                                <td style={styles.tableCell}> {/* Display booking status (Occupied/Available) here */}
                                  <span style={{ ...styles.status, ...getStatusStyleForRoom(room.status) }}>
                                    {room.status}
                                  </span>
                                </td>
                                <td style={styles.tableCell}>{room.roomType}</td>
                                <td style={styles.tableCell}>
                                    <svg
                                        style={styles.ellipsisIcon}
                                        onClick={() => handleEditClick(room)}
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20" height="20" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                        onMouseOver={(e) => e.currentTarget.style.color = styles.ellipsisIconHover.color}
                                        onMouseOut={(e) => e.currentTarget.style.color = styles.ellipsisIcon.color}
                                    >
                                        <circle cx="12" cy="12" r="1"></circle>
                                        <circle cx="12" cy="5" r="1"></circle>
                                        <circle cx="12" cy="19" r="1"></circle>
                                    </svg>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={styles.pagination}>
                <button
                  style={{
                    ...styles.paginationBtn,
                    ...(page === 1 ? styles.paginationBtnDisabled : {}),
                  }}
                  onClick={() => setPage(p => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  onMouseOver={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = styles.paginationBtnHover.backgroundColor; }}
                  onMouseOut={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = styles.paginationBtn.backgroundColor; }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Previous
                </button>
                <div style={styles.pageNumbers}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                        <div
                          key={n}
                          style={{
                            ...styles.pageNumber,
                            ...(n === page ? styles.pageNumberActive : {}),
                          }}
                          onClick={() => setPage(n)}
                          onMouseOver={(e) => { if (n !== page) e.currentTarget.style.backgroundColor = styles.pageNumberHover.backgroundColor; }}
                          onMouseOut={(e) => { if (n !== page) e.currentTarget.style.backgroundColor = styles.pageNumber.backgroundColor; }}
                        >
                            {n}
                        </div>
                    ))}
                </div>
                <button
                  style={{
                    ...styles.paginationBtn,
                    ...(page === totalPages ? styles.paginationBtnDisabled : {}),
                  }}
                  onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages}
                  onMouseOver={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = styles.paginationBtnHover.backgroundColor; }}
                  onMouseOut={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = styles.paginationBtn.backgroundColor; }}
                >
                    Next
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>

            {/* Add/Edit Room Modal */}
            {addEditModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <button style={styles.modalCloseButton} onClick={() => setAddEditModalOpen(false)}>&times;</button>
                        <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                            {currentRoom ? 'Edit Room' : 'Add New Room'}
                        </h3>
                        {[
                            { name: 'roomNumber', label: 'Room Number', type: 'text' },
                            { name: 'bedType', label: 'Bed Type', type: 'text' }, // Re-added
                            { name: 'roomFloor', label: 'Room Floor', type: 'text' },
                            { name: 'roomFacility', label: 'Room Facility', type: 'text' },
                            { name: 'roomType', label: 'Room Type', type: 'text' },
                        ].map((field) => (
                            <div key={field.name} style={{ marginBottom: '16px' }}>
                                <label style={styles.modalLabel}>{field.label}</label>
                                {field.name === 'roomNumber' && currentRoom ? (
                                    <select
                                        name={field.name}
                                        value={formData[field.name] || ''}
                                        onChange={handleInputChange}
                                        style={styles.modalSelect}
                                    >
                                        <option value="">Select a Room Number</option>
                                        {/* Always include the current room number if it's being edited, regardless of its status */}
                                        {currentRoom && <option value={currentRoom.roomNumber}>{currentRoom.roomNumber} (Current)</option>}
                                        {/* Filter for only available rooms for editing, ensuring they match roomType and are not the current room (if already listed) */}
                                        {rooms.filter(r =>
                                            r.status === 'Available' &&
                                            r.roomNumber !== (currentRoom ? currentRoom.roomNumber : null)
                                        ).map(r => (
                                            <option key={r.id} value={r.roomNumber}>{r.roomNumber}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        name={field.name}
                                        value={formData[field.name] || ''}
                                        onChange={handleInputChange}
                                        type={field.type}
                                        style={styles.modalInput}
                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                        readOnly={currentRoom && field.name === 'roomNumber'}
                                    />
                                )}
                            </div>
                        ))}

                        {/* Status Dropdown (for booking status: Occupied/Available) */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.modalLabel}>Booking Status</label>
                            <select
                                name="status"
                                value={formData.status || ''}
                                onChange={handleInputChange}
                                style={styles.modalSelect}
                            >
                                <option value="">Select Booking Status</option>
                                <option value="Reserved">Reserved</option>
                                <option value="Booked">Booked</option>
                                <option value="Checked In">Checked In</option>
                                <option value="Checked Out">Checked Out</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="New">New</option>
                                <option value="Available">Available</option>
                            </select>
                        </div>

                        {/* New Room Status Dropdown (for cleanliness: clean/dirty/service) */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.modalLabel}>Cleanliness Status</label>
                            <select
                                name="roomStatus"
                                value={formData.roomStatus || ''}
                                onChange={handleInputChange}
                                style={styles.modalSelect}
                            >
                                <option value="">Select Cleanliness Status</option>
                                <option value="clean">Clean</option>
                                <option value="durty">Dirty</option>
                                <option value="service">Service</option>
                            </select>
                        </div>

                        <div style={styles.modalButtonsContainer}>
                            <button
                                onClick={() => setAddEditModalOpen(false)}
                                style={styles.modalCancelBtn}
                                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; e.currentTarget.style.borderColor = '#9CA3AF'; }}
                                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = styles.modalCancelBtn.backgroundColor; e.currentTarget.style.borderColor = styles.modalCancelBtn.borderColor; }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                style={styles.modalSaveBtn}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.modalSaveBtn['&:hover']?.backgroundColor || '#9A6B46'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.modalSaveBtn.backgroundColor}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoomList;